import { useMemo } from 'react';

// context
import { AppContextProvider } from '@/components/utilities/AppContext';

// components
import Page404 from './Page404';
import Page500 from './Page500';
import PageHead from './PageHead';
import ComponentsRender from '@/components/ComponentsRender';
import ScrollSensor from '@/components/utilities/ScrollSensor';
import GlossaryDetailPage from '@/components/components/glossary/GlossaryDetailPage';

// constants
import { LAYOUT_BLOCKS } from '@/components/components';

// utils
import { buildFaqPageSchema, buildArticleSchema } from '@/utils/json-ld-schemas';
import { getGlossaryRootForLocale } from '@/utils/glossary-routes';
import { getGlossaryLabels } from '@/utils/glossary-labels';
import { collectFaqItems } from '@/utils/faq-items';

const NAV_TYPENAMES = new Set(['ComponentLayoutBlocksNavigationBlock']);
const FOOTER_TYPENAMES = new Set(['ComponentLayoutBlocksFooterBlock']);
const QUESTIONS_BLOCK_TYPENAME = 'ComponentComponentsQuestionsBlock';
const GLOSSARY_FAQ_TYPENAME = 'ComponentGlossaryFaq';

const Page = ({
  pageData,
  error,
  locale,
  rootPath,
  restOfPath,
  queryParams,
  isPreview,
  glossaryTerm = null,
  glossaryTerms = null,
  glossarySettings = null
}) => {
  const appContext = useMemo(
    () => ({
      locale,
      rootPath,
      restOfPath,
      queryParams,
      glossaryTerm,
      glossaryTerms,
      glossarySettings
    }),
    [locale, restOfPath, rootPath, queryParams, glossaryTerm, glossaryTerms, glossarySettings]
  );

  if (error) {
    return <Page500 error={error} />;
  }

  if (!pageData) {
    return <Page404 />;
  }

  const { title, description, path, localizations, seoTitle, seoDescription, ogImage, noIndex } = pageData;
  const content = pageData.content || [];

  const isGlossaryDetail = Boolean(glossaryTerm);

  // Prefer SEO-specific fields from Strapi; fall back to the regular title/description.
  // Marketing can write keyword-optimised metadata without touching the page H1/intro.
  const headTitle = isGlossaryDetail
    ? glossaryTerm.seoTitle || glossaryTerm.title
    : seoTitle || title;
  const headDescription = isGlossaryDetail
    ? glossaryTerm.seoDescription || glossaryTerm.shortDescription
    : seoDescription || description;
  const headOgImage = isGlossaryDetail ? glossaryTerm.ogImage : ogImage;
  const headNoIndex = isGlossaryDetail ? glossaryTerm.noIndex : noIndex;

  // Collect FAQPage questions from all FAQ blocks on the page. Page content
  // nests QuestionsBlock inside LayoutBlock containers and possibly inside a
  // SharedComponent; glossary content is flat but uses a different __typename
  // and `items` instead of `questions`. The recursive walker handles both.
  const faqQuestions = isGlossaryDetail
    ? collectFaqItems(glossaryTerm.content, GLOSSARY_FAQ_TYPENAME, 'items')
    : collectFaqItems(content, QUESTIONS_BLOCK_TYPENAME, 'questions');

  // Build extra JSON-LD schemas for this page: FAQPage (if any FAQ blocks) and
  // Article (for glossary detail pages). PageHead always adds Organization,
  // WebSite, WebPage, and BreadcrumbList by default.
  const headPath = isGlossaryDetail
    ? `${getGlossaryRootForLocale(locale)}/${glossaryTerm.slug}`
    : path;

  const glossaryRootPath = isGlossaryDetail ? getGlossaryRootForLocale(locale) : null;
  const localePrefix = locale === 'cs' ? '' : `/${locale}`;
  const glossaryRootUrl = isGlossaryDetail
    ? `${process.env.NEXT_PUBLIC_SITE_URL}${localePrefix}${glossaryRootPath}`
    : null;
  const glossaryTermUrl = isGlossaryDetail ? `${glossaryRootUrl}/${glossaryTerm.slug}` : null;

  const glossaryLabels = isGlossaryDetail ? getGlossaryLabels(glossarySettings) : null;
  const headBreadcrumbs = isGlossaryDetail
    ? [
        {
          name: glossaryLabels.rootLabel,
          url: glossaryRootUrl
        },
        {
          name: glossaryTerm.title,
          url: glossaryTermUrl
        }
      ]
    : null;

  // Glossary localizations have {locale, slug} shape; remap to {locale, path}
  // so PageHead's hreflang generator can use them uniformly.
  const headLocalizations = isGlossaryDetail
    ? (glossaryTerm.localizations || []).map((loc) => ({
        locale: loc.locale,
        path: `${getGlossaryRootForLocale(loc.locale)}/${loc.slug}`
      }))
    : localizations;

  const extraSchemas = [
    buildFaqPageSchema(faqQuestions),
    isGlossaryDetail &&
      buildArticleSchema({
        title: glossaryTerm.title,
        description: glossaryTerm.shortDescription,
        canonicalUrl: glossaryTermUrl,
        locale,
        publishedAt: glossaryTerm.publishedAt,
        updatedAt: glossaryTerm.updatedAt
      })
  ].filter(Boolean);

  return (
    <>
      <PageHead
        title={headTitle}
        description={headDescription}
        path={headPath}
        locale={locale}
        localizations={headLocalizations}
        isPreview={isPreview}
        ogImage={headOgImage}
        noIndex={headNoIndex}
        breadcrumbs={headBreadcrumbs}
        extraSchemas={extraSchemas}
      />

      <AppContextProvider value={appContext}>
        <ScrollSensor>
          {isGlossaryDetail ? (
            <>
              {content
                .filter((component) => NAV_TYPENAMES.has(component.__typename))
                .map((component) => (
                  <ComponentsRender
                    key={`${component.id}_${component.__typename}`}
                    type={LAYOUT_BLOCKS}
                    component={component}
                  />
                ))}

              <GlossaryDetailPage />

              {content
                .filter((component) => FOOTER_TYPENAMES.has(component.__typename))
                .map((component) => (
                  <ComponentsRender
                    key={`${component.id}_${component.__typename}`}
                    type={LAYOUT_BLOCKS}
                    component={component}
                  />
                ))}
            </>
          ) : (
            content.map((component) => (
              <ComponentsRender
                key={`${component.id}_${component.__typename}`}
                type={LAYOUT_BLOCKS}
                component={component}
              />
            ))
          )}
        </ScrollSensor>
      </AppContextProvider>
    </>
  );
};
export default Page;
